import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ArrowLeft, Tag, Truck, Shield, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { validateCoupon } from '../services/couponService';
import { getShippingSettings } from '../services/shippingSettingsService';
import { translateProductName } from '../utils/translateProductName';

const Cart = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { 
    items, 
    total, 
    shipping, 
    finalTotal, 
    itemCount, 
    isLoading, 
    zipCode,
    isCalculatingShipping,
    updateQuantity, 
    removeFromCart,
    setZipCode 
  } = useCart();
  
  console.log('🛒 Cart Debug:', { items, itemCount, isLoading, total });
  
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; discount: number } | null>(null);
  const [localZipCode, setLocalZipCode] = useState(zipCode || '');
  const [couponError, setCouponError] = useState<string>('');
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [freeShippingThreshold, setFreeShippingThreshold] = useState(50);
  const [amountNeeded, setAmountNeeded] = useState(0);
  const [processingDays, setProcessingDays] = useState(3);

  useEffect(() => {
    const loadShippingSettings = async () => {
      try {
        const settings = await getShippingSettings();
        if (settings) {
          setFreeShippingThreshold(settings.free_shipping_threshold);
          setProcessingDays(settings.processing_days);
        }
      } catch (error) {
        console.error('Erro ao carregar configurações de frete:', error);
      }
    };
    
    loadShippingSettings();
  }, []);

  useEffect(() => {
    if (total > 0) {
      const needed = Math.max(0, freeShippingThreshold - total);
      setAmountNeeded(needed);
    }
  }, [total, freeShippingThreshold]);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;

    setIsValidatingCoupon(true);
    setCouponError('');

    try {
      const result = await validateCoupon(couponCode, total, user?.email);
      
      if (result.valid && result.coupon) {
        setAppliedCoupon({ 
          code: result.coupon.code, 
          discount: result.discount ?? 0
        });
        setCouponCode('');
      } else {
        setCouponError(result.error || t('coupon.invalid'));
      }
    } catch (error: any) {
      setCouponError(error.message || t('coupon.invalid'));
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const calculateDiscountedTotal = () => {
    if (!appliedCoupon) return total;
    return Math.max(0, total - appliedCoupon.discount);
  };

  const cartFinalTotal = () => {
    const discountedTotal = calculateDiscountedTotal();
    const shippingCost = shipping || 0;
    return discountedTotal + shippingCost;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-green-50 flex items-center justify-center">
        <div className="text-center">
          <ShoppingBag className="mx-auto h-24 w-24 text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('cart.empty')}</h2>
          <Link 
            to="/produtos"
            className="inline-flex items-center gap-2 mt-4 bg-cyan-600 text-white px-6 py-3 rounded-lg hover:bg-cyan-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            {t('cart.continueShopping')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-green-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('cart.title')}</h1>
              <p className="text-gray-600 mt-1">{itemCount} {itemCount === 1 ? t('common.item') : t('common.items')}</p>
            </div>
            <Link 
              to="/produtos"
              className="flex items-center gap-2 text-cyan-600 hover:text-cyan-700 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              {t('cart.continueShopping')}
            </Link>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {items.map((item) => (
                <div key={item.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                  <div className="flex gap-4">
                    <img 
                      src={item.product?.image_url} 
                      alt={item.product?.name}
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-semibold text-gray-900">{translateProductName(item.product?.name || '')}</h3>
                          <p className="text-sm text-gray-600">{translateProductName(item.product?.description || '')}</p>
                        </div>
                        <button
                          onClick={() => item.id && removeFromCart(item.id)}
                          className="text-red-500 hover:text-red-700 transition-colors p-1.5 hover:bg-red-50 rounded-lg"
                          title={t('cart.remove')}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="inline-flex items-center gap-2 bg-gray-100 rounded-lg p-1.5">
                          <button
                            onClick={() => item.id && updateQuantity(item.id, Math.max(0, item.quantity - 1))}
                            className="p-1.5 hover:bg-white rounded-md transition-colors inline-flex items-center justify-center"
                            aria-label={t('common.decrease')}
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="font-semibold min-w-[2rem] text-center">{item.quantity}</span>
                          <button
                            onClick={() => item.id && updateQuantity(item.id, item.quantity + 1)}
                            className="p-1.5 hover:bg-white rounded-md transition-colors inline-flex items-center justify-center"
                            aria-label={t('common.increase')}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <span className="text-xl font-bold text-cyan-600">
                          CHF {((item.product?.price || 0) * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-md p-6 sticky top-4 space-y-4">
                {/* Coupon Section */}
                <div className="pb-4 border-b">
                  <div className="flex items-center gap-2 mb-3">
                    <Tag className="w-5 h-5 text-cyan-600" />
                    <h3 className="font-semibold">{t('coupon.code')}</h3>
                  </div>
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder={t('coupon.code')}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#4ECDC4] focus:border-[#4ECDC4] text-sm font-medium"
                      disabled={!!appliedCoupon || isValidatingCoupon}
                    />
                    <button
                      onClick={handleApplyCoupon}
                      disabled={!couponCode.trim() || !!appliedCoupon || isValidatingCoupon}
                      className="w-full px-6 py-3 bg-gradient-to-r from-[#4ECDC4] to-[#95C11F] text-white rounded-xl hover:shadow-lg disabled:bg-gray-300 disabled:bg-none disabled:text-gray-500 disabled:cursor-not-allowed transition-all font-semibold text-sm inline-flex items-center justify-center"
                    >
                      {isValidatingCoupon ? '...' : t('coupon.apply')}
                    </button>
                  </div>
                  {couponError && (
                    <p className="text-red-500 text-sm mt-2">{couponError}</p>
                  )}
                  {appliedCoupon && (
                    <div className="mt-2 flex items-center justify-between bg-green-50 p-2 rounded">
                      <span className="text-green-700 text-sm">
                        {t('coupon.applied', { code: appliedCoupon.code, discount: appliedCoupon.discount })}
                      </span>
                      <button
                        onClick={() => setAppliedCoupon(null)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Shipping Calculator */}
                <div className="pb-4 border-b">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-5 h-5 text-cyan-600" />
                    <h3 className="font-semibold">{t('cart.calculateShipping')}</h3>
                  </div>
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={localZipCode}
                      onChange={(e) => setLocalZipCode(e.target.value)}
                      placeholder={t('cart.enterPostalCode')}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#4ECDC4] focus:border-[#4ECDC4] text-sm font-medium"
                      disabled={isCalculatingShipping}
                    />
                    <button
                      onClick={() => setZipCode(localZipCode)}
                      disabled={!localZipCode.trim() || isCalculatingShipping}
                      className="w-full px-6 py-3 bg-gradient-to-r from-[#4ECDC4] to-[#95C11F] text-white rounded-xl hover:shadow-lg disabled:bg-gray-300 disabled:bg-none disabled:text-gray-500 disabled:cursor-not-allowed transition-all font-semibold text-sm inline-flex items-center justify-center"
                    >
                      {isCalculatingShipping ? '...' : t('common.ok')}
                    </button>
                  </div>
                  
                  {/* Free Shipping Progress */}
                  {amountNeeded > 0 && (
                    <div className="mt-3 bg-cyan-50 p-3 rounded-lg">
                      <p className="text-sm text-cyan-800 mb-2">
                        {t('cart.amountForFreeShipping', { amount: amountNeeded.toFixed(2) })}
                      </p>
                      <div className="w-full bg-cyan-200 rounded-full h-2">
                        <div 
                          className="bg-cyan-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${Math.min((total / freeShippingThreshold) * 100, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                  
                  {amountNeeded === 0 && total >= freeShippingThreshold && (
                    <div className="mt-3 bg-green-50 p-3 rounded-lg">
                      <p className="text-sm text-green-700 font-medium">
                        {t('cart.freeShippingUnlocked')}
                      </p>
                    </div>
                  )}
                </div>

                {/* Order Summary */}
                <div className="space-y-3">
                  <div className="flex justify-between text-gray-600">
                    <span>{t('cart.subtotal')}:</span>
                    <span>CHF {total.toFixed(2)}</span>
                  </div>
                  
                  {appliedCoupon && (
                    <div className="flex justify-between text-green-600">
                      <span>{t('cart.discount')} (-{appliedCoupon.discount}%):</span>
                      <span>- CHF {(total * (appliedCoupon.discount / 100)).toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-gray-600">
                    <span>{t('cart.shipping')}:</span>
                    <span>
                      {shipping === null && !zipCode ? (
                        <span className="text-amber-600">{t('cart.calculateFirst')}</span>
                      ) : shipping === null && zipCode ? (
                        <span className="text-red-600">{t('cart.shippingUnavailable', 'Região não atendida')}</span>
                      ) : shipping === 0 ? (
                        <span className="text-green-600">{t('cart.freeShipping')}</span>
                      ) : (
                        `CHF ${shipping!.toFixed(2)}`
                      )}
                    </span>
                  </div>
                  
                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold">{t('cart.total')}:</span>
                      <span className="text-2xl font-bold text-cyan-600">CHF {cartFinalTotal().toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Processing Time Info */}
                <div className="flex items-center gap-2 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
                  <Truck className="w-5 h-5 text-blue-600" />
                  <span>{t('cart.processingTime')}: {processingDays} {t('cart.businessDays')}</span>
                </div>

                {/* Checkout Button */}
                <Link
                  to="/checkout"
                  className={`w-full inline-flex items-center justify-center gap-2 py-4 rounded-xl font-bold transition-all text-base shadow-md hover:shadow-xl transform hover:-translate-y-0.5 ${
                    shipping === null
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-gradient-to-r from-[#4ECDC4] to-[#95C11F] text-white hover:from-[#45b8b0] hover:to-[#88b519]'
                  }`}
                  onClick={(e) => {
                    if (shipping === null) {
                      e.preventDefault();
                    }
                  }}
                >
                  <Shield className="w-5 h-5 flex-shrink-0" />
                  <span className="leading-none">{t('cart.checkout')}</span>
                </Link>
                
                <p className="text-center text-sm text-gray-500">
                  {t('cart.secureCheckout')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
